import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Users, 
  Gift, 
  DollarSign, 
  Activity,
  Shield,
  Settings,
  BarChart3,
  AlertCircle,
  Map,
  Lock,
  Palette
} from 'lucide-react';
import { api, UserProfile } from '../lib/api';
import { toast } from 'sonner';

interface AdminDashboardProps {
  onNavigate: (screen: string) => void;
}

export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalGiftGroups: 0,
    totalTransactions: 0,
    platformRevenue: 0,
  });

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      
      // Load user profile to get admin level
      const profile = await api.getProfile();
      setUserProfile(profile);
      
      // Check if user is actually an admin
      if (!profile.adminLevel) {
        toast.error('Access Denied', {
          description: 'You do not have admin privileges',
        });
        onNavigate('dashboard');
        return;
      }
      
      // TODO: Implement admin stats endpoint
      // const response = await api.get('/admin/stats');
      // setStats(response);
      
      // Placeholder data for now
      setStats({
        totalUsers: 0,
        totalGiftGroups: 0,
        totalTransactions: 0,
        platformRevenue: 0,
      });
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin dashboard');
      onNavigate('dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getAdminLevelBadge = () => {
    if (!userProfile?.adminLevel) return null;
    
    switch (userProfile.adminLevel) {
      case 1:
        return <Badge className="bg-red-600">Super Admin</Badge>;
      case 2:
        return <Badge className="bg-orange-600">Admin</Badge>;
      case 3:
        return <Badge className="bg-yellow-600">Support</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-secondary/30 p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-secondary/30 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 text-primary" />
              <h1 className="text-emerald-700">System Administration</h1>
            </div>
            <div className="flex items-center gap-2">
              {getAdminLevelBadge()}
              {userProfile?.adminLevel && (
                <span className="text-sm text-muted-foreground">
                  Access Level {userProfile.adminLevel}
                </span>
              )}
            </div>
          </div>
          <Button onClick={() => onNavigate('dashboard')} variant="outline">
            Back to Dashboard
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl">{stats.totalUsers}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Gift className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gift Groups</p>
                <p className="text-2xl">{stats.totalGiftGroups}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Transactions</p>
                <p className="text-2xl">{stats.totalTransactions}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Revenue</p>
                <p className="text-2xl">${stats.platformRevenue.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Admin Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-5">
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="groups">
              <Gift className="w-4 h-4 mr-2" />
              Gift Groups
            </TabsTrigger>
            <TabsTrigger value="dev-tools">
              <Palette className="w-4 h-4 mr-2" />
              Dev Tools
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">Platform Overview</h2>
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Admin analytics and reporting coming in Phase 3
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">User Management</h2>
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  User administration tools coming in Phase 3
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="groups" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">Gift Group Management</h2>
              <div className="text-center py-12">
                <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Gift group administration tools coming in Phase 3
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="dev-tools" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">Development Tools</h2>
              <p className="text-muted-foreground mb-6">
                Access design system documentation, authentication flows, and user journey maps.
              </p>
              
              <div className="grid gap-4">
                {/* User Flow Map */}
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <button
                    onClick={() => onNavigate("flow-map")}
                    className="w-full text-left"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Map className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl mb-1">User Flow Map</h3>
                        <p className="text-sm text-muted-foreground">
                          Interactive visualization of all user journeys through the platform, including gift creation, acceptance, and contribution flows.
                        </p>
                      </div>
                    </div>
                  </button>
                </Card>

                {/* Authentication Demo */}
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <button
                    onClick={() => onNavigate("auth-showcase")}
                    className="w-full text-left"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Lock className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl mb-1">Authentication Demo</h3>
                        <p className="text-sm text-muted-foreground">
                          Demonstration of magic link authentication, registration flow, and session management with real examples.
                        </p>
                      </div>
                    </div>
                  </button>
                </Card>

                {/* Design System */}
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <button
                    onClick={() => onNavigate("design-showcase")}
                    className="w-full text-left"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Palette className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl mb-1">Design System</h3>
                        <p className="text-sm text-muted-foreground">
                          Complete component library with colors, typography, buttons, forms, and all UI elements used throughout the platform.
                        </p>
                      </div>
                    </div>
                  </button>
                </Card>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card className="p-6">
              <h2 className="mb-4">System Settings</h2>
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm">
                        <strong>Admin Access Levels:</strong>
                      </p>
                      <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        <li>• <strong>Level 1 (Super Admin):</strong> Full system access</li>
                        <li>• <strong>Level 2 (Admin):</strong> Standard admin privileges</li>
                        <li>• <strong>Level 3 (Support):</strong> Limited admin access</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="text-center py-8">
                  <Settings className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    System configuration tools coming in Phase 3
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}